/**
 * Cómo se llega al honorario de un médico. MedicalProtocol no lo saca del
 * contrato del médico sino del consumo del protocolo que lo origina:
 *
 *   facturado al sponsor  (sourceConsumptionAmount)
 *   − retención           (sourceWithholdingAmount)
 *   − costo descontado    (discountedCostAmount)
 *   = base de cálculo     (calculationBaseAmount)
 *   × porcentaje pactado  (percentageApplied)
 *   = honorario           (amount)
 *
 * Los mismos campos viajan en el consumo del médico y en el detalle de su
 * liquidación, por eso esta lógica vive en commons y no en una pantalla.
 */

export interface FeeSource {
    amount?: number | null;
    percentageApplied?: number | null;
    sourceConsumptionAmount?: number | null;
    sourceWithholdingAmount?: number | null;
    discountedCostAmount?: number | null;
    calculationBaseAmount?: number | null;
}

export interface FeeBreakdown {
    source: number;
    withholding: number;
    discountedCost: number;
    base: number;
    percentage: number;
    fee: number;
    /** El honorario no coincide con base × % (redondeo aparte): hubo ajuste manual o cotización. */
    hasManualAdjustment: boolean;
}

const num = (value: number | null | undefined): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/**
 * Hay base de cálculo cuando el servidor informó de dónde sale el honorario.
 * Un consumo cargado a mano, o uno anterior al cambio, viene con todo en 0 y
 * no tiene nada que explicar.
 */
export const hasFeeBreakdown = (item: FeeSource | null | undefined): boolean => {
    if (!item) return false;
    return num(item.sourceConsumptionAmount) !== 0 || num(item.calculationBaseAmount) !== 0;
};

export const feeBreakdown = (item: FeeSource): FeeBreakdown => {
    const source = num(item.sourceConsumptionAmount);
    const withholding = num(item.sourceWithholdingAmount);
    const discountedCost = num(item.discountedCostAmount);
    // Si el servidor no informó la base pero sí el origen, se reconstruye.
    const base = item.calculationBaseAmount !== undefined && item.calculationBaseAmount !== null
        ? num(item.calculationBaseAmount)
        : source - withholding - discountedCost;
    const percentage = num(item.percentageApplied);
    const fee = num(item.amount);
    const expected = Math.round(base * percentage) / 100;
    return {
        source,
        withholding,
        discountedCost,
        base,
        percentage,
        fee,
        hasManualAdjustment: percentage > 0 && Math.abs(expected - fee) > 0.01,
    };
};

/** "35% de USD 1.200,00" para leer de un vistazo en una celda. */
export const feeShortLabel = (breakdown: FeeBreakdown, formatAmount: (amount: number) => string): string => {
    if (breakdown.percentage <= 0) return formatAmount(breakdown.base);
    return `${formatPercentage(breakdown.percentage)} de ${formatAmount(breakdown.base)}`;
};

export const formatPercentage = (value: number): string =>
    `${Number.isInteger(value) ? value : value.toLocaleString('es-AR', { maximumFractionDigits: 2 })}%`;

/**
 * Cotización aplicada al honorario, si la hubo. El consumo del médico trae
 * `rate` y el detalle de liquidación `exchangeRate`; los dos traen la moneda y
 * el importe originales. Sin moneda original distinta no hubo conversión.
 */
export interface ConversionSource {
    currencyCode?: string | null;
    currency?: string | null;
    originalCurrencyCode?: string | null;
    originalAmount?: number | null;
    rate?: number | null;
    exchangeRate?: number | null;
    rateType?: number | null;
}

export interface AppliedConversion {
    originalCurrency: string;
    originalAmount: number;
    rate: number;
    rateType?: number;
}

export const conversionOf = (item: ConversionSource | null | undefined): AppliedConversion | undefined => {
    if (!item) return undefined;
    const original = (item.originalCurrencyCode || '').trim().toUpperCase();
    const current = (item.currencyCode ?? item.currency ?? '').trim().toUpperCase();
    const rate = num(item.rate ?? item.exchangeRate);
    if (!original || original === current || rate <= 0) return undefined;
    return {
        originalCurrency: original,
        originalAmount: num(item.originalAmount),
        rate,
        rateType: typeof item.rateType === 'number' ? item.rateType : undefined,
    };
};

/** RateTypeEnum: 0 fecha de factura, 1 fecha de pago, 2 manual, 3 automática. */
export const rateTypeLabel = (rateType: number | undefined): string | undefined => {
    switch (rateType) {
        case 0: return 'cotización a fecha de factura';
        case 1: return 'cotización a fecha de pago';
        case 2: return 'cotización manual';
        case 3: return 'cotización automática';
        default: return undefined;
    }
};

export const formatRate = (rate: number): string =>
    rate.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** "USD 100,00 × 1.250,00" para poner debajo del importe convertido. */
export const conversionLabel = (conversion: AppliedConversion, formatAmount: (amount: number, currency: string) => string): string =>
    `${formatAmount(conversion.originalAmount, conversion.originalCurrency)} × ${formatRate(conversion.rate)}`;

/** Suma de bases por moneda, para subtotales de tablas. */
export const sumFeeBases = <T extends FeeSource & { currencyCode?: string | null; currency?: string | null }>(items: T[]): Map<string, { base: number; fee: number }> => {
    const totals = new Map<string, { base: number; fee: number }>();
    items.forEach((item) => {
        const currency = (item.currencyCode ?? item.currency ?? '').toUpperCase();
        const current = totals.get(currency) ?? { base: 0, fee: 0 };
        const breakdown = feeBreakdown(item);
        current.base += breakdown.base;
        current.fee += breakdown.fee;
        totals.set(currency, current);
    });
    return totals;
};
